import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Typography, Box, IconButton, Popover, Chip, Dialog, DialogTitle, DialogContent,
  Tabs, Tab, Button, Tooltip, ToggleButton, ToggleButtonGroup, CircularProgress
} from '@mui/material';
import {
  Sparkles, Users, Send, LogOut, Wrench, HardDrive, Layers,
  BookMarked, BookOpen, Menu, X, UserRound, ChevronDown, ChevronLeft, ChevronRight,
  Gamepad2, GitBranch, Monitor, Smartphone, RotateCcw,
  Settings, Bot, ScrollText, Camera, CheckCircle2, XCircle, Clock, Cpu, FileText, User, Copy, Ban,
  Package, Image as ImageIcon
} from 'lucide-react';
import { useAuthContext } from '../context/AuthContext';
import WorkflowsStub from '../components/WorkflowsStub';
import TelegramBotsPanel from '../components/TelegramBotsPanel';
import AiToolsPanel from '../components/AiToolsPanel';
import StoragePanel from '../components/StoragePanel';
import TeamsPanel from '../components/TeamsPanel';
import UsersPanel from '../components/UsersPanel';
import CharactersPanel from '../components/CharactersPanel';
import StoryflowPanel from '../components/StoryflowPanel';
import SavesPanel from '../components/SavesPanel';
import ConfigPanel from '../components/ConfigPanel';
import BuildPanel from '../components/BuildPanel';
import GenerationPanel from '../components/GenerationPanel';
import EmbeddingsPanel from '../components/EmbeddingsPanel';
import NexusChat from '../components/NexusChat';
import InstaPanel from '../components/InstaPanel';
import { AdminPanelSkeleton, useCachedPanelCount } from '../components/adminPanelContent';
import { API_BASE_URL } from '../utils/api';
import { logWarn } from '../utils/log';
import { WorkflowsProvider } from '../context/WorkflowsContext';
import { TeamProvider, useTeam } from '../context/TeamContext';
import { SCREEN_PRESETS } from '../game/gameTemplates';

/** All possible nav items with their icons */
const NAV_ITEM_DEFS = {
  teams: { label: 'Teams', Icon: Users, adminOnly: true },
  users: { label: 'Users', Icon: UserRound, adminOnly: true },
  'ai-logs': { label: 'AI Logs', Icon: ScrollText },
  telegram: { label: 'Telegram bots', Icon: Send },
  'ai-tools': { label: 'AI tools', Icon: Wrench },
  storage: { label: 'Storage', Icon: HardDrive },
  embeddings: { label: 'Embeddings', Icon: Layers },
  nexus: { label: 'Nexus', Icon: Bot },
  workflows: { label: 'Workflows', Icon: Sparkles },
  characters: { label: 'Characters', Icon: UserRound },
  storyflow: { label: 'Chapters', Icon: BookMarked },
  insta: { label: 'Insta', Icon: Camera },
  'image-generation': { label: 'Generation', Icon: ImageIcon },
  config: { label: 'Config', Icon: Settings, gameOnly: true },
  play: { label: 'Play', Icon: Gamepad2, gameOnly: true },
  saves: { label: 'Saves', Icon: GitBranch, gameOnly: true },
  build: { label: 'Build', Icon: Package, gameOnly: true }
};

const PLAY_GROUP_ICONS = { Portrait: Smartphone, Landscape: Monitor };

function TierDropdown({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  const items = [{ id: '', label: 'Free' }, ...options.map((t) => ({ id: t, label: t }))];
  const current = items.find((i) => i.id === value) || items[0];
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
          value ? 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-500/30' : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/80'
        }`}
      >
        <ChevronDown size={12} strokeWidth={2} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        <span>{current.label}</span>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 min-w-[160px] rounded-lg border border-zinc-700 bg-zinc-900 shadow-xl z-50 py-1 overflow-hidden">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => { onChange(item.id); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
                item.id === value
                  ? 'bg-amber-500/15 text-amber-400 font-semibold'
                  : 'text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PlayModal({ open, onClose, teamId, patreonTiers }) {
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [activePresetId, setActivePresetId] = useState('3:4');
  const [iframeKey, setIframeKey] = useState(0);
  const [testTier, setTestTier] = useState('');
  const testTierInit = useRef(false);
  useEffect(() => {
    if (testTierInit.current) return;
    if ((patreonTiers || []).length > 0) { setTestTier(patreonTiers[patreonTiers.length - 1]); testTierInit.current = true; }
  }, [patreonTiers]);

  const activePreset = useMemo(
    () => SCREEN_PRESETS.find((p) => p.id === activePresetId) || SCREEN_PRESETS[0],
    [activePresetId]
  );

  const gameSrc = useMemo(() => {
    const params = new URLSearchParams();
    if (teamId) params.set('teamId', teamId);
    if (testTier) params.set('testTier', testTier);
    return `/game?${params.toString()}`;
  }, [teamId, testTier]);

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed', inset: 0, zIndex: 1300,
        display: 'flex', flexDirection: 'column',
        bgcolor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      {/* Toolbar */}
      <div
        className="flex items-center gap-2 border-b border-zinc-800/90 bg-zinc-900/95 px-3 py-2 shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          title="Close"
          className="flex items-center justify-center rounded p-1.5 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 transition-colors mr-1"
        >
          <X size={18} strokeWidth={2} />
        </button>
        <div className="h-5 w-px bg-zinc-800 mx-1" />
        {!isNarrow && (
          <div className="flex items-center gap-1 flex-wrap">
            {SCREEN_PRESETS.map((preset) => {
              const active = preset.id === activePresetId;
              const Icon = PLAY_GROUP_ICONS[preset.group] || Monitor;
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => { setActivePresetId(preset.id); }}
                  title={`${preset.device} (${preset.label})`}
                  className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                    active
                      ? 'bg-violet-500/15 text-violet-400 ring-1 ring-violet-500/30'
                      : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/80'
                  }`}
                >
                  <Icon size={13} strokeWidth={2} />
                  <span>{preset.label}</span>
                </button>
              );
            })}
          </div>
        )}
        {!isNarrow && <div className="h-5 w-px bg-zinc-800 mx-1" />}
        {/* Tier selector */}
        <TierDropdown value={testTier} options={patreonTiers || []} onChange={(v) => { setTestTier(v); setIframeKey((k) => k + 1); }} />
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setIframeKey((k) => k + 1)}
          title="Reload game"
          className="flex items-center justify-center rounded p-1.5 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-colors"
        >
          <RotateCcw size={16} strokeWidth={2} />
        </button>
      </div>
      {/* Game viewport — fills remaining area */}
      <div
        className="flex-1 flex items-center justify-center overflow-hidden min-h-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="relative overflow-hidden"
          style={{
            aspectRatio: `${activePreset.w} / ${activePreset.h}`,
            height: '100%',
            maxWidth: '100%',
          }}
        >
          <iframe
            key={iframeKey}
            src={gameSrc}
            title="Game"
            className="absolute inset-0 w-full h-full"
            style={{ border: 'none' }}
          />
        </div>
      </div>
    </Box>
  );
}

function AdminPageInner({ userRole }) {
  const { getToken, signOut } = useAuthContext();
  const { teams, activeTeam, activeTeamId, switchTeam, loading: teamsLoading } = useTeam();
  const [searchParams, setSearchParams] = useSearchParams();
  const [me, setMe] = useState(null);

  const hasGame = !!(activeTeam?.gameTemplate);

  const navItems = useMemo(() => {
    const addGameItems = (items) => {
      if (hasGame) {
        items.push({ id: 'config', ...NAV_ITEM_DEFS.config });
        items.push({ id: 'saves', ...NAV_ITEM_DEFS.saves });
        items.push({ id: 'build', ...NAV_ITEM_DEFS.build });
        items.push({ id: 'play', ...NAV_ITEM_DEFS.play });
      }
      return items;
    };

    if (userRole === 'admin') {
      const items = [
        { id: 'teams', ...NAV_ITEM_DEFS.teams },
        { id: 'users', ...NAV_ITEM_DEFS.users }
      ];
      if (activeTeam) items.push({ id: 'ai-logs', ...NAV_ITEM_DEFS['ai-logs'] });
      const features = activeTeam?.features || [];
      const featureOrder = ['telegram', 'ai-tools', 'storage', 'embeddings', 'nexus', 'workflows', 'characters', 'storyflow', 'insta', 'image-generation'];
      for (const fId of featureOrder) {
        // Nexus appears when embeddings is enabled
        const featureKey = fId === 'nexus' ? 'embeddings' : fId;
        if (features.includes(featureKey) && NAV_ITEM_DEFS[fId]) {
          items.push({ id: fId, ...NAV_ITEM_DEFS[fId] });
        }
      }
      return addGameItems(items);
    }
    const features = activeTeam?.features || [];
    const featureOrder = ['telegram', 'ai-tools', 'storage', 'embeddings', 'nexus', 'workflows', 'characters', 'storyflow', 'insta', 'image-generation'];
    const items = [];
    if (activeTeam) items.push({ id: 'ai-logs', ...NAV_ITEM_DEFS['ai-logs'] });
    for (const fId of featureOrder) {
      if (NAV_ITEM_DEFS[fId]?.adminOnly) continue;
      const featureKey = fId === 'nexus' ? 'embeddings' : fId;
      if (features.includes(featureKey) && NAV_ITEM_DEFS[fId]) {
        items.push({ id: fId, ...NAV_ITEM_DEFS[fId] });
      }
    }
    return addGameItems(items);
  }, [userRole, activeTeam, hasGame]);

  const [activeNavId, setActiveNavId] = useState(() => {
    // Priority: ?nav= query > localStorage > role default.
    const navParam = searchParams.get('nav');
    if (navParam && navParam !== 'play' && NAV_ITEM_DEFS[navParam]) return navParam;
    try {
      const stored = localStorage.getItem('flowcore_admin_nav');
      if (stored && stored !== 'play' && NAV_ITEM_DEFS[stored]) return stored;
    } catch (_) { /* ignore */ }
    return userRole === 'admin' ? 'teams' : 'workflows';
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [playOpen, setPlayOpen] = useState(false);

  // Consume nav query param on mount (clean URL)
  useEffect(() => {
    const navParam = searchParams.get('nav');
    if (navParam) {
      if (navParam === 'play') {
        setPlayOpen(true);
      } else if (NAV_ITEM_DEFS[navParam]) {
        setActiveNavId(navParam);
      }
      searchParams.delete('nav');
      setSearchParams(searchParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Team switcher popover
  const [teamAnchor, setTeamAnchor] = useState(null);

  const selectNav = useCallback((id) => {
    if (id === 'play') {
      setPlayOpen(true);
      setSidebarOpen(false);
      return;
    }
    setActiveNavId(id);
    setSidebarOpen(false);
  }, []);

  // Persist the active section so refreshes land on the same panel.
  useEffect(() => {
    if (!activeNavId || activeNavId === 'play') return;
    try { localStorage.setItem('flowcore_admin_nav', activeNavId); } catch (_) { /* ignore */ }
  }, [activeNavId]);

  // Reset active nav if it's no longer in the list (only once team is loaded)
  useEffect(() => {
    if (activeTeam && navItems.length > 0 && !navItems.find((n) => n.id === activeNavId)) {
      setActiveNavId(navItems[0].id);
    }
  }, [navItems, activeNavId, activeTeam]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/api/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setMe(data);
      } catch (e) {
        logWarn('admin', '/api/users/me (header profile):', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  const panelClass =
    'rounded-xl border border-zinc-800/90 bg-zinc-900/50 p-5 shadow-lg shadow-black/25 backdrop-blur-sm';

  const navRowBase =
    'flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm font-medium transition-colors min-h-[2rem]';
  const navRowInactive = `${navRowBase} border border-transparent text-zinc-400 hover:bg-zinc-800/80 hover:text-zinc-200`;
  const navRowActive = `${navRowBase} border border-transparent bg-violet-500/10 text-violet-400`;

  const sidebarContent = (
    <>
      <div className="border-b border-zinc-800/90 px-2.5 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-0 min-w-0 flex-1">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500 leading-snug shrink-0">
            Flowcore
          </span>
          {activeTeam && (
            <>
              <span className="text-[11px] text-zinc-600 mx-1 shrink-0">&mdash;</span>
              <button
                type="button"
                onClick={(e) => setTeamAnchor(e.currentTarget)}
                className="text-[11px] font-semibold uppercase tracking-[0.14em] text-violet-400 hover:text-violet-300 transition-colors leading-snug truncate flex items-center gap-0.5 bg-transparent border-none cursor-pointer p-0"
              >
                <span className="truncate">{activeTeam.name}</span>
                <ChevronDown size={12} strokeWidth={2} className="shrink-0 opacity-60" />
              </button>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => setSidebarOpen(false)}
          className="md:hidden flex items-center justify-center rounded p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          <X size={18} strokeWidth={2} />
        </button>
      </div>
      <nav className="vr-scroll-y min-h-0 flex-1 space-y-0.5 overflow-y-auto p-1.5">
        {navItems.map((item) => {
          const NavIcon = item.Icon;
          const active = activeNavId === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => selectNav(item.id)}
              className={active ? navRowActive : navRowInactive}
            >
              {NavIcon ? (
                <NavIcon
                  size={20}
                  strokeWidth={2}
                  className="shrink-0"
                  style={{ color: active ? 'rgb(167, 139, 250)' : undefined }}
                />
              ) : null}
              <span className="min-w-0 leading-snug">{item.label}</span>
            </button>
          );
        })}
      </nav>
      <div className="px-1.5 pb-1">
        <button
          type="button"
          onClick={() => { setSidebarOpen(false); window.location.href = '/docs'; }}
          className={`${navRowBase} border border-transparent text-zinc-500 hover:bg-zinc-800/80 hover:text-zinc-300`}
        >
          <BookOpen size={18} strokeWidth={2} className="shrink-0" />
          <span className="min-w-0 leading-snug">Docs</span>
        </button>
      </div>
      <div className="border-t border-zinc-800/90 px-2 py-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {me?.picture ? (
            <img
              src={me.picture}
              alt=""
              className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-zinc-700"
            />
          ) : (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-800 text-xs font-medium text-zinc-400">
              {(me?.displayName || me?.username || '?').slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-medium text-zinc-200">
              {me?.displayName || me?.username || 'Account'}
            </div>
            {me?.username ? (
              <div className="truncate text-[11px] text-zinc-500">@{me.username}</div>
            ) : null}
          </div>
          <IconButton
            size="small"
            onClick={() => signOut()}
            aria-label="Log out"
            title="Log out"
            sx={{ color: 'text.secondary', flexShrink: 0 }}
          >
            <LogOut size={20} strokeWidth={2} />
          </IconButton>
        </div>
      </div>
    </>
  );

  return (
    <WorkflowsProvider getToken={getToken} teamId={activeTeamId}>
    <div className="flex h-screen w-full overflow-hidden bg-zinc-950 text-zinc-200">
      {/* mobile header */}
      <div className="fixed top-0 left-0 right-0 z-30 flex md:hidden items-center gap-2 border-b border-zinc-800/90 bg-zinc-900/95 backdrop-blur px-3 py-2">
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="flex items-center justify-center rounded p-1 text-zinc-400 hover:text-zinc-200 transition-colors"
        >
          <Menu size={20} strokeWidth={2} />
        </button>
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">Flowcore</span>
        {activeTeam && (
          <>
            <span className="text-[11px] text-zinc-600">&mdash;</span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-violet-400">{activeTeam.name}</span>
          </>
        )}
      </div>

      {/* mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* sidebar — desktop: static, mobile: overlay */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 flex w-56 flex-col border-r border-zinc-800/90 bg-zinc-900
        transition-transform duration-200 ease-in-out
        md:static md:translate-x-0 md:shrink-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {sidebarContent}
      </aside>

      {/* Team switcher popover */}
      <Popover
        open={!!teamAnchor}
        anchorEl={teamAnchor}
        onClose={() => setTeamAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: '#18181b',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              minWidth: 180,
              boxShadow: '0 10px 40px rgba(0,0,0,0.45)',
              backgroundImage: 'none'
            }
          }
        }}
      >
        <Box sx={{ py: 0.75, px: 0.5 }}>
          <Typography variant="caption" sx={{ px: 1.5, pt: 0.5, pb: 1, color: 'text.disabled', fontWeight: 700, fontSize: '0.6rem', letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block' }}>
            Switch team
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {teams.map((t) => {
              const active = t.id === activeTeamId;
              return (
                <Box
                  key={t.id}
                  onClick={() => { switchTeam(t.id); setTeamAnchor(null); }}
                  sx={{
                    px: 1.5,
                    py: 1,
                    mx: 0.5,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    bgcolor: active ? 'rgba(139, 92, 246, 0.12)' : 'transparent',
                    border: active ? '1px solid rgba(139, 92, 246, 0.25)' : '1px solid transparent',
                    '&:hover': { bgcolor: active ? 'rgba(139, 92, 246, 0.16)' : 'rgba(255,255,255,0.04)' },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    transition: 'all 0.15s ease'
                  }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: active ? '#a78bfa' : 'rgba(255,255,255,0.15)', flexShrink: 0 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: active ? 600 : 400,
                      color: active ? '#a78bfa' : 'text.primary',
                      fontSize: '0.85rem'
                    }}
                  >
                    {t.name}
                  </Typography>
                  {t.isDefault && (
                    <Chip label="lab" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, ml: 'auto', bgcolor: 'rgba(139,92,246,0.1)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.2)' }} />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Popover>

      <main className="vr-scroll-y min-h-0 min-w-0 flex-1 overflow-y-auto pt-11 md:pt-0">
        <div className="mx-auto w-full max-w-6xl px-5 py-5 max-md:px-2 max-md:py-2">
          {!teamsLoading && userRole !== 'admin' && teams.length === 0 && (
            <div className={panelClass}>
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
                <Users size={40} style={{ color: 'rgba(167,139,250,0.3)' }} />
                <Typography variant="h6" sx={{ color: 'text.primary' }}>
                  No team assigned
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 360 }}>
                  Your account has member access, but you haven't been added to any team yet.
                  Ask an administrator to add you to a team.
                </Typography>
              </div>
            </div>
          )}
          {userRole === 'admin' && activeNavId === 'teams' && (
            <div className={panelClass}>
              <TeamsPanel getToken={getToken} />
            </div>
          )}

          {userRole === 'admin' && activeNavId === 'users' && (
            <div className={panelClass}>
              <UsersPanel getToken={getToken} />
            </div>
          )}

          {activeNavId === 'ai-logs' && activeTeam && (
            <div className={panelClass}>
              <AiLogsPanel
                getToken={getToken}
                teamId={activeTeamId}
                teamName={activeTeam.name}
                userRole={userRole}
              />
            </div>
          )}

          {activeNavId === 'telegram' && (
            <div className={panelClass}>
              <TelegramBotsPanel getToken={getToken} />
            </div>
          )}

          {activeNavId === 'ai-tools' && (
            <div className={panelClass}>
              <AiToolsPanel getToken={getToken} />
            </div>
          )}

          {activeNavId === 'storage' && (
            <div className={panelClass}>
              <StoragePanel getToken={getToken} />
            </div>
          )}

          {activeNavId === 'embeddings' && (
            <div className={panelClass}>
              <EmbeddingsPanel getToken={getToken} />
            </div>
          )}

          {activeNavId === 'nexus' && (
            <div className={`${panelClass} max-md:!p-0 max-md:!bg-transparent max-md:!border-0 max-md:!shadow-none max-md:!backdrop-blur-none max-md:!rounded-none`} style={{ height: 'calc(100vh - 6rem)' }}>
              <NexusChat getToken={getToken} />
            </div>
          )}

          {activeNavId === 'workflows' && (
            <div className={panelClass}>
              <WorkflowsStub />
            </div>
          )}

          {activeNavId === 'characters' && (
            <div className={panelClass}>
              <CharactersPanel getToken={getToken} />
            </div>
          )}

          {activeNavId === 'storyflow' && (
            <div className={panelClass}>
              <StoryflowPanel getToken={getToken} />
            </div>
          )}

          {activeNavId === 'insta' && (
            <div className={panelClass}>
              <InstaPanel getToken={getToken} />
            </div>
          )}

          {activeNavId === 'image-generation' && (
            <div className={panelClass}>
              <GenerationPanel getToken={getToken} />
            </div>
          )}

          {activeNavId === 'config' && (
            <div className={panelClass}>
              <ConfigPanel getToken={getToken} />
            </div>
          )}

          {activeNavId === 'saves' && (
            <div className={panelClass}>
              <SavesPanel getToken={getToken} />
            </div>
          )}

          {activeNavId === 'build' && (
            <div className={panelClass}>
              <BuildPanel getToken={getToken} />
            </div>
          )}

        </div>
      </main>
      {activeTeam && (
        <PlayModal open={playOpen} onClose={() => setPlayOpen(false)} teamId={activeTeam.id} patreonTiers={activeTeam?.gameConfig?.patreonTiers} />
      )}
    </div>
    </WorkflowsProvider>
  );
}

const AdminPage = ({ userRole }) => {
  const { getToken } = useAuthContext();

  return (
    <TeamProvider getToken={getToken} userRole={userRole}>
      <AdminPageInner userRole={userRole} />
    </TeamProvider>
  );
};

/* ═══════════════════════════════════════════════════════════════
   AI LOGS PANEL — per-team, visible to every team member.
   Every AI call (success / error / rejected) is logged with full
   context. Click a row to open the detail dialog with tabs:
     • Instruction — system prompt
     • Request     — user prompt / messages
     • Response    — model output
     • Meta        — task, assistant, model, route, duration, tokens
   ═══════════════════════════════════════════════════════════════ */

const PAGE_SIZE = 10;

function fmtLogDate(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
}

function providerChipSx(provider) {
  if (provider === 'grok') return { bgcolor: 'rgba(99,102,241,0.15)', color: '#818cf8' };
  if (provider === 'gemini') return { bgcolor: 'rgba(52,211,153,0.15)', color: '#34d399' };
  return { bgcolor: 'rgba(161,161,170,0.15)', color: '#a1a1aa' };
}

function LogStatusChip({ status, httpStatus }) {
  if (status === 'pending') {
    return (
      <Chip
        icon={<CircularProgress size={10} thickness={5} sx={{ color: '#60a5fa !important' }} />}
        label="pending"
        size="small"
        sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'rgba(96,165,250,0.15)', color: '#60a5fa', '& .MuiChip-icon': { ml: 0.6, mr: -0.3 } }}
      />
    );
  }
  if (status === 'success') {
    return (
      <Chip
        icon={<CheckCircle2 size={12} strokeWidth={2.5} />}
        label="success"
        size="small"
        sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'rgba(52,211,153,0.15)', color: '#34d399', '& .MuiChip-icon': { color: '#34d399', ml: 0.5 } }}
      />
    );
  }
  if (status === 'rejected') {
    return (
      <Chip
        icon={<Ban size={12} strokeWidth={2.5} />}
        label="rejected"
        size="small"
        sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'rgba(251,191,36,0.15)', color: '#fbbf24', '& .MuiChip-icon': { color: '#fbbf24', ml: 0.5 } }}
      />
    );
  }
  const label = httpStatus ? `error ${httpStatus}` : 'error';
  const warn = httpStatus === 429;
  return (
    <Chip
      icon={<XCircle size={12} strokeWidth={2.5} />}
      label={label}
      size="small"
      sx={{
        height: 20, fontSize: '0.6rem', fontWeight: 700,
        bgcolor: warn ? 'rgba(251,191,36,0.15)' : 'rgba(248,113,113,0.15)',
        color: warn ? '#fbbf24' : '#f87171',
        '& .MuiChip-icon': { color: warn ? '#fbbf24' : '#f87171', ml: 0.5 }
      }}
    />
  );
}

/** Formatted section inside the detail dialog. */
function LogSection({ title, icon, value, mono = true, empty = '—' }) {
  const display = value == null || value === '' ? empty : value;
  const isEmpty = display === empty;
  const handleCopy = useCallback(() => {
    if (!isEmpty && typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(String(display)).catch(() => {});
    }
  }, [display, isEmpty]);
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        {icon}
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
          {title}
        </Typography>
        {!isEmpty && (
          <Tooltip title="Copy">
            <IconButton size="small" onClick={handleCopy} sx={{ ml: 'auto', color: 'text.disabled' }}>
              <Copy size={13} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Box
        className="vr-scroll-y"
        sx={{
          p: 1.5,
          borderRadius: '8px',
          border: '1px solid',
          borderColor: 'rgba(255,255,255,0.08)',
          bgcolor: 'rgba(0,0,0,0.25)',
          maxHeight: 420,
          overflowY: 'auto',
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
          fontSize: mono ? '0.72rem' : '0.8rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          color: isEmpty ? 'text.disabled' : 'text.primary',
          lineHeight: 1.55,
        }}
      >
        {display}
      </Box>
    </Box>
  );
}

function LogDetailDialog({ log, onClose }) {
  const [tab, setTab] = useState(0);
  useEffect(() => { setTab(0); }, [log?._id]);

  if (!log) return null;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          backgroundImage: 'none',
        }
      }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={log.provider || '?'} size="small" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, ...providerChipSx(log.provider) }} />
          <LogStatusChip status={log.status} httpStatus={log.httpStatus} />
          {log.model && (
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{log.model}</Typography>
          )}
          {log.task && (
            <Chip label={log.task} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(139,92,246,0.12)', color: '#a78bfa' }} />
          )}
          <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', ml: 'auto' }}>
            {fmtLogDate(log.timestamp)}
          </Typography>
        </Box>
        {log.assistant && (
          <Typography sx={{ mt: 0.75, fontSize: '0.85rem', color: 'text.primary', fontWeight: 500 }}>
            {log.assistant}
          </Typography>
        )}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
      >
        <X size={18} />
      </IconButton>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<FileText size={14} />} iconPosition="start" label="Instruction" sx={{ minHeight: 42, textTransform: 'none' }} />
          <Tab icon={<User size={14} />} iconPosition="start" label="Request" sx={{ minHeight: 42, textTransform: 'none' }} />
          <Tab icon={<Bot size={14} />} iconPosition="start" label="Response" sx={{ minHeight: 42, textTransform: 'none' }} />
          <Tab icon={<Cpu size={14} />} iconPosition="start" label="Meta" sx={{ minHeight: 42, textTransform: 'none' }} />
        </Tabs>
      </Box>
      <DialogContent sx={{ pt: 2 }}>
        {tab === 0 && (
          <LogSection
            title="System instruction"
            icon={<FileText size={14} style={{ color: '#a78bfa' }} />}
            value={log.instruction}
          />
        )}
        {tab === 1 && (
          <LogSection
            title="User request"
            icon={<User size={14} style={{ color: '#818cf8' }} />}
            value={log.request}
          />
        )}
        {tab === 2 && (
          <>
            {(log.status === 'error' || log.status === 'rejected') && log.error && (
              <LogSection
                title={log.status === 'rejected' ? 'Rejection reason' : 'Error message'}
                icon={log.status === 'rejected'
                  ? <Ban size={14} style={{ color: '#fbbf24' }} />
                  : <XCircle size={14} style={{ color: '#f87171' }} />}
                value={log.error}
                mono={false}
              />
            )}
            <LogSection
              title={log.status === 'error' ? 'Response (raw / error detail)' : 'Model response'}
              icon={<Bot size={14} style={{ color: '#34d399' }} />}
              value={log.response || log.detail}
            />
          </>
        )}
        {tab === 3 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <MetaRow label="Task" value={log.task} />
            <MetaRow label="Assistant" value={log.assistant} />
            <MetaRow label="Provider" value={log.provider} />
            <MetaRow label="Model" value={log.model} />
            <MetaRow label="Route" value={log.route} />
            <MetaRow label="Team" value={log.teamId} />
            <MetaRow label="Status" value={log.status} />
            <MetaRow label="HTTP status" value={log.httpStatus} />
            <MetaRow label="Duration" value={log.durationMs != null ? `${log.durationMs} ms` : null} />
            <MetaRow label="Prompt tokens" value={log.promptTokens} />
            <MetaRow label="Completion tokens" value={log.completionTokens} />
            <MetaRow label="Total tokens" value={log.totalTokens} />
            <MetaRow label="Timestamp" value={fmtLogDate(log.timestamp)} />
            <MetaRow label="Log id" value={log._id} mono />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Single row in the Meta tab. Hidden entirely when value is empty. */
function MetaRow({ label, value, mono = false }) {
  if (value == null || value === '') return null;
  const display = String(value);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 2, alignItems: 'baseline' }}>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled' }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.8rem',
          color: 'text.primary',
          fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined,
          wordBreak: 'break-all',
        }}
      >
        {display}
      </Typography>
    </Box>
  );
}

function AiLogsPanel({ getToken, teamId, teamName, userRole }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all'); // all | success | error | rejected
  const [page, setPage] = useState(0); // 0-indexed
  const [selected, setSelected] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [cachedLogCount, setCachedLogCount] = useCachedPanelCount(
    teamId ? `ai-logs:${teamId}:${statusFilter}` : null,
    8
  );

  const isAdmin = userRole === 'admin';

  // Reset pagination whenever the filter or team changes.
  useEffect(() => { setPage(0); }, [statusFilter, teamId]);

  // load(silent=true) skips the loading skeleton — used by the polling tick
  // so the list doesn't flicker between full data and skeleton every few
  // seconds while a pending log is in flight.
  const load = useCallback(async (silent = false) => {
    if (!teamId) return;
    if (!silent) setLoading(true);
    try {
      const token = await getToken();
      const qs = new URLSearchParams({
        teamId,
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
      });
      if (statusFilter !== 'all') qs.set('status', statusFilter);
      const res = await fetch(`${API_BASE_URL}/api/ai-logs?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const logList = data.logs || [];
        setLogs(logList);
        setTotal(data.total || 0);
        setSuccessCount(data.successCount || 0);
        setErrorCount(data.errorCount || 0);
        setRejectedCount(data.rejectedCount || 0);
        setPendingCount(data.pendingCount || 0);
        setCachedLogCount(logList.length);
      }
    } catch (e) { /* ignore */ }
    if (!silent) setLoading(false);
  }, [getToken, teamId, statusFilter, page, setCachedLogCount]);

  useEffect(() => { load(); }, [load]);

  // Auto-poll while there are in-flight calls so the user sees pending →
  // terminal transitions live without having to mash refresh. We poll every
  // 3s (silent — no skeleton flash) and stop the moment pendingCount drops
  // to 0. The dependency on `pendingCount` re-arms the effect each time the
  // count changes, which is the simplest way to start/stop without stale
  // closures.
  useEffect(() => {
    if (!teamId || pendingCount === 0) return undefined;
    const id = setInterval(() => { load(true); }, 3000);
    return () => clearInterval(id);
  }, [teamId, pendingCount, load]);

  // When the user has a pending log open in the detail dialog, swap in the
  // freshly polled version so they can watch the response/usage/timing fill
  // in without closing and reopening.
  useEffect(() => {
    if (!selected || selected.status !== 'pending') return;
    const fresh = logs.find((l) => l._id === selected._id);
    if (fresh && fresh !== selected) setSelected(fresh);
  }, [logs, selected]);

  const handleClear = useCallback(async () => {
    const token = await getToken();
    await fetch(`${API_BASE_URL}/api/ai-logs?teamId=${encodeURIComponent(teamId)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    setConfirmClear(false);
    setPage(0);
    load();
  }, [getToken, teamId, load]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const firstOnPage = total === 0 ? 0 : currentPage * PAGE_SIZE + 1;
  const lastOnPage = Math.min(total, (currentPage + 1) * PAGE_SIZE);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ color: '#a78bfa', display: 'flex', alignItems: 'center' }}>
          <ScrollText size={22} strokeWidth={2} />
        </Box>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
          AI Logs
        </Typography>
        {teamName && (
          <Chip label={teamName} size="small" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, bgcolor: 'rgba(139,92,246,0.12)', color: '#a78bfa' }} />
        )}
        <Box sx={{ display: 'flex', gap: 0.75, ml: 'auto', alignItems: 'center' }}>
          <IconButton size="small" onClick={load} title="Refresh"><RotateCcw size={16} /></IconButton>
          {isAdmin && total > 0 && (
            <IconButton size="small" onClick={() => setConfirmClear(true)} title="Clear team logs" sx={{ color: 'error.main' }}>
              <X size={16} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          size="small"
          onChange={(_, v) => v && setStatusFilter(v)}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontSize: '0.72rem',
              py: 0.5,
              px: 1.5,
              color: 'text.secondary',
              borderColor: 'rgba(255,255,255,0.1)',
              gap: 0.75,
            },
            '& .MuiToggleButton-root.Mui-selected': {
              bgcolor: 'rgba(139,92,246,0.15)',
              color: '#a78bfa',
            }
          }}
        >
          <ToggleButton value="all">All ({successCount + errorCount + rejectedCount + pendingCount})</ToggleButton>
          {pendingCount > 0 && (
            <ToggleButton value="pending">
              <CircularProgress size={10} thickness={5} sx={{ color: '#60a5fa' }} />
              Pending ({pendingCount})
            </ToggleButton>
          )}
          <ToggleButton value="success">
            <CheckCircle2 size={12} style={{ color: '#34d399' }} />
            Success ({successCount})
          </ToggleButton>
          <ToggleButton value="rejected">
            <Ban size={12} style={{ color: '#fbbf24' }} />
            Rejected ({rejectedCount})
          </ToggleButton>
          <ToggleButton value="error">
            <XCircle size={12} style={{ color: '#f87171' }} />
            Error ({errorCount})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading && <AdminPanelSkeleton variant="log" count={cachedLogCount} />}

      {!loading && logs.length === 0 && (
        <Typography color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 6 }}>
          No entries — no AI calls yet.
        </Typography>
      )}

      {!loading && logs.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {logs.map((log) => {
            const isError = log.status === 'error';
            const isRejected = log.status === 'rejected';
            const isPending = log.status === 'pending';
            const accent = isError
              ? { bg: 'rgba(248,113,113,0.05)', bd: 'rgba(248,113,113,0.18)', bgH: 'rgba(248,113,113,0.09)', bdH: 'rgba(248,113,113,0.3)', txt: 'rgba(248,113,113,0.85)' }
              : isRejected
                ? { bg: 'rgba(251,191,36,0.05)', bd: 'rgba(251,191,36,0.18)', bgH: 'rgba(251,191,36,0.09)', bdH: 'rgba(251,191,36,0.3)', txt: 'rgba(251,191,36,0.85)' }
                : isPending
                  ? { bg: 'rgba(96,165,250,0.05)', bd: 'rgba(96,165,250,0.22)', bgH: 'rgba(96,165,250,0.09)', bdH: 'rgba(96,165,250,0.35)', txt: 'rgba(96,165,250,0.85)' }
                  : { bg: 'rgba(255,255,255,0.02)', bd: 'rgba(255,255,255,0.06)', bgH: 'rgba(139,92,246,0.08)', bdH: 'rgba(139,92,246,0.25)', txt: 'rgba(250,250,250,0.55)' };
            const previewText = isError || isRejected ? (log.error || log.request || '') : (log.request || '');
            return (
              <Box
                key={log._id}
                onClick={() => setSelected(log)}
                sx={{
                  p: 1.25,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  bgcolor: accent.bg,
                  border: '1px solid',
                  borderColor: accent.bd,
                  transition: 'all 0.12s ease',
                  minWidth: 0,
                  '&:hover': { bgcolor: accent.bgH, borderColor: accent.bdH }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={log.provider} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, ...providerChipSx(log.provider) }} />
                  <LogStatusChip status={log.status} httpStatus={log.httpStatus} />
                  {log.sandbox && (
                    <Chip label="Sandbox" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'rgba(251,146,60,0.15)', color: '#fb923c', border: '1px solid rgba(251,146,60,0.3)' }} />
                  )}
                  {log.task && (
                    <Chip label={log.task} size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(139,92,246,0.1)', color: '#a78bfa' }} />
                  )}
                  {log.assistant && (
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.primary', fontWeight: 500 }}>
                      {log.assistant}
                    </Typography>
                  )}
                  {log.model && (
                    <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>{log.model}</Typography>
                  )}
                  {log.durationMs != null && (
                    <Tooltip title="Duration">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: 'text.disabled' }}>
                        <Clock size={10} />
                        <Typography sx={{ fontSize: '0.62rem' }}>{log.durationMs}ms</Typography>
                      </Box>
                    </Tooltip>
                  )}
                  {log.totalTokens != null && (
                    <Tooltip title={`Tokens: ${log.promptTokens ?? '?'} in / ${log.completionTokens ?? '?'} out`}>
                      <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>
                        {log.totalTokens}t
                      </Typography>
                    </Tooltip>
                  )}
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', ml: 'auto' }}>
                    {fmtLogDate(log.timestamp)}
                  </Typography>
                </Box>
                {previewText && (
                  <Typography
                    sx={{
                      mt: 0.5,
                      fontSize: '0.72rem',
                      color: accent.txt,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: 0,
                    }}
                  >
                    {previewText}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {!loading && total > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, gap: 2 }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>
            {firstOnPage}–{lastOnPage} of {total}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              size="small"
              disabled={currentPage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              sx={{ color: 'text.secondary' }}
            >
              <ChevronLeft size={16} />
            </IconButton>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', minWidth: 50, textAlign: 'center' }}>
              {currentPage + 1} / {pageCount}
            </Typography>
            <IconButton
              size="small"
              disabled={currentPage >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              sx={{ color: 'text.secondary' }}
            >
              <ChevronRight size={16} />
            </IconButton>
          </Box>
        </Box>
      )}

      <LogDetailDialog log={selected} onClose={() => setSelected(null)} />

      <Dialog
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', backgroundImage: 'none' } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Clear AI logs</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            All AI log entries for team <strong>{teamName}</strong> will be permanently deleted.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 3, justifyContent: 'flex-end' }}>
            <Button onClick={() => setConfirmClear(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleClear}>
              Clear
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default AdminPage;
